import { Component, Prop, State, Event, Watch, h } from '@stencil/core';
import {
  SharpFullStar,
  SharpEmptyStar,
  SharpHalfStar,
  SharpQuarterStar,
  SharpThreeQuarterStar,
  RoundFullStar,
  RoundHalfStar,
  RoundEmptyStar,
  RoundQuarterStar,
  RoundThreeQuarterStar,
} from '../../../icons/index';
import { isRtl } from '@ugc-utils/utils';

enum VariantType {
  SharpCorner = 'SharpCorner',
  RoundCorner = 'RoundCorner',
}

const StarType = {
  Full: 0,
  Half: 1,
  Empty: 2,
  Quarter: 3,
  ThreeQuarter: 4,
};

const Star = ({ type, size, fill, variant }) => {
  const starMap = {
    [VariantType.SharpCorner]: new Map([
      [StarType.Full, <SharpFullStar width={size} height={size} fill={fill} />],
      [StarType.Half, <SharpHalfStar width={size} height={size} fill={fill} />],
      [StarType.Empty, <SharpEmptyStar width={size} height={size} fill={fill} />],
      [StarType.Quarter, <SharpQuarterStar width={size} height={size} fill={fill} />],
      [StarType.ThreeQuarter, <SharpThreeQuarterStar width={size} height={size} fill={fill} />],
    ]),
    [VariantType.RoundCorner]: new Map([
      [StarType.Full, <RoundFullStar width={size} height={size} />],
      [StarType.Half, <RoundHalfStar width={size} height={size} />],
      [StarType.Empty, <RoundEmptyStar width={size} height={size} />],
      [StarType.Quarter, <RoundQuarterStar width={size} height={size} />],
      [StarType.ThreeQuarter, <RoundThreeQuarterStar width={size} height={size} />],
    ]),
  };
  return starMap[variant].get(type) || null;
};

@Component({
  tag: 'ugc-star',
  styleUrl: 'ugc-star.scss',
  shadow: false,
})
export class UgcStar {
  @Prop() realValue: number = 0;
  @Prop() disabled: boolean = false;
  @Prop() withTips: boolean = true;
  @Prop() rateDescriptions: string[] = [];
  @Prop() count: number = 5;
  @Prop() size: number = 48;
  @Prop() hoverValue: number;
  @Prop() rtl: boolean = false;
  @Prop() starColor: string = '#FAC627';
  @Prop() starItemStyle: any = {};
  @Prop() variant?: VariantType.SharpCorner | VariantType.RoundCorner = VariantType.SharpCorner;

  @State() value: number = 0;

  @Event() handleStarClick;

  componentWillLoad() {
    this.setValue();
  }

  @Watch('realValue')
  modifyRealValue() {
    this.setValue();
  }

  @Watch('hoverValue')
  modifyHoverValue() {
    this.setValue();
  }

  setValue() {
    this.value = this.hoverValue || this.realValue;

    const trunc = Math.trunc(this.value);
    const fraction = Number(Number(this.value % 1).toFixed(2));
    if (fraction <= 0.19) {
      this.value = trunc;
    } else if (fraction >= 0.2 && fraction <= 0.29) {
      this.value = Number(`${trunc}.25`);
    } else if (fraction >= 0.3 && fraction <= 0.69) {
      this.value = Number(`${trunc}.5`);
    } else if (fraction >= 0.7 && fraction <= 0.79) {
      this.value = Number(`${trunc}.75`);
    } else if (fraction >= 0.8) {
      this.value = trunc + 1;
    }
  }

  getType(index) {
    const percent = this.value - index;

    if (percent >= 1) {
      return StarType.Full;
    } else if (percent >= 0.75) {
      return StarType.ThreeQuarter;
    } else if (percent >= 0.5) {
      return StarType.Half;
    } else if (percent >= 0.25) {
      return StarType.Quarter;
    } else {
      return StarType.Empty;
    }
  }

  render() {
    const starItemStyle = isRtl()
      ? { transform: 'rotateY(-180deg)', ...this.starItemStyle }
      : { transform: 'none', ...this.starItemStyle };

    const stars = [];
    for (let i = 0; i < this.count; i += 1) {
      const type = this.getType(i);
      stars.push(
        <li
          class="ugc-star-item"
          key={`star-${i}`}
          style={starItemStyle}
          onClick={() => this.handleStarClick.emit(i + 1)}
        >
          <Star size={this.size} type={type} fill={this.starColor} variant={this.variant} />
        </li>
      );
    }
    const publicStyle = { minWidth: `${this.count * this.size}px` };
    let ratePoor = '';
    let rateGreat = '';
    let rateValue = '';
    let tipColor = '#999';
    if (this.rateDescriptions && this.rateDescriptions.length) {
      [ratePoor, , , , rateGreat] = this.rateDescriptions;
      if (this.value) {
        rateValue = this.rateDescriptions[this.value - 1];
        tipColor = '#333';
      }
    }

    const renderWithTips = this.withTips && (
      <div class="tips-wrap" style={publicStyle}>
        <div class="tip" style={{ color: tipColor }}>
          {rateValue || ratePoor}
        </div>
        {!this.value && <div class="tip">{rateGreat}</div>}
      </div>
    );

    return (
      <div>
        <ul
          class={{
            'ugc-star-wrap': true,
            'flex-row-start-center': true,
            'round-corner': this.variant === VariantType.RoundCorner,
          }}
          style={{ ...publicStyle }}
        >
          {stars}
        </ul>
        {renderWithTips}
      </div>
    );
  }
}
